import { HeartHandshake, ClipboardPlus, Building2, Check, ArrowRight } from "lucide-react";
import { Reveal, Eyebrow, SectionHeading } from "@/components/Reveal";
import { scrollToId } from "@/lib/analytics";

const SUITES = [
  {
    icon: HeartHandshake,
    tag: "For Certified Peer Support Specialists",
    title: "The Peer Support Specialist Suite",
    desc: "Prompts tailored to the CPSS scope of practice — the documentation peer professionals actually produce every day.",
    items: ["Progress Notes", "Crisis Calls", "Visit Attempts", "Collateral Contacts"],
  },
  {
    icon: ClipboardPlus,
    tag: "For Qualified Professionals & Team Leads",
    title: "The QP / Team Lead Suite",
    desc: "Everything in the Peer Support Suite, plus advanced prompts for supervisory and clinical documentation.",
    items: ["Everything in Peer Support", "Person-Centered Plan (PCP) Details", "Comprehensive Clinical Assessments (CCA)"],
  },
  {
    icon: Building2,
    tag: "For Whole Organizations",
    title: "Agency Custom Integration",
    desc: "Full agency onboarding — we tune our prompt architecture to your specific requirements.",
    items: ["State Medicaid billing alignment", "Audit-policy-specific formatting", "Team-wide rollout & training"],
  },
];

export function RoleSuites() {
  return (
    <section id="role-suites" className="bg-slate-50 py-20 lg:py-28" aria-labelledby="suites-heading">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl">
          <Eyebrow>Built for Specific Clinical Roles</Eyebrow>
          <SectionHeading id="suites-heading">Tailored for Scope of Practice</SectionHeading>
          <p className="mt-5 text-base leading-relaxed text-slate-600">
            Different roles require entirely different documentation. VeraChart's role-specific documentation workflows
            are built around the actual scope of practice of each team member — not a one-size-fits-all template.
            Built for community-based behavioral health teams, including ACT, PACT, FACT, CSP, peer support, housing,
            employment, nursing, and other multidisciplinary programs.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-6 lg:grid-cols-3" data-testid="role-suite-cards">
          {SUITES.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.12}>
              <div className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-7 transition-all hover:-translate-y-1 hover:border-teal-300 hover:shadow-xl hover:shadow-navy/5">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-50 text-teal-700">
                  <s.icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <p className="mt-5 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-teal-700">{s.tag}</p>
                <h3 className="mt-2 font-display text-xl font-bold tracking-tight text-navy">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                <ul className="mt-5 flex-1 space-y-2.5">
                  {s.items.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-slate-700">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" aria-hidden="true" />
                      {item}
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  data-testid={`suite-pricing-link-${i}`}
                  onClick={() => scrollToId("pricing")}
                  className="mt-6 inline-flex items-center gap-1.5 self-start text-sm font-semibold text-teal-700 transition-colors hover:text-navy"
                >
                  See what's included <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
